import {
  getDefaultColumnDescription,
  getNameIdColumn,
  getStatusDesColumn,
  getStringArrayColumn
} from 'components/TableColumn/TableColumn';
import {
  CampaignAdTypeSettingFormData,
  CampaignAdTypeState,
  ChannelGroupFormData,
  ChannelType,
  ScreenDisplayType
} from 'core/channelGroup/ChannelGroup';
import { useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from 'i18n';
import { DefaultChannelGroupManager } from 'core/channelGroup/ChannelGroupManager';
import { useCallAPI } from 'hooks/useCallAPI';
import { camelCase, compact, isEmpty } from 'lodash';

export enum ChannelGroupAdTypeListColumns {
  ID = 'id',
  SCREEN_DISPLAY_TYPE = 'screenDisplayType',
  DURATION = 'duration',
  SIZE = 'size',
  OUTDOOR_TYPE = 'supportedOutdoorTypes',
  DELIVERY_UNIT = 'deliveryUnit',
  STATE = 'state',
  PRICING_MODEL = 'pricingModel'
}

const channelGroupManager = new DefaultChannelGroupManager();

export const useChannelGroupAdTypeListModel = () => {
  const { values, setValues } = useFormikContext<ChannelGroupFormData>();
  const { loading, callAPIs } = useCallAPI();
  const [adTypesUsageStatus, setAdTypesUsageStatus] = useState<
    Record<number, boolean>
  >({});
  const [adTypeToEdit, setAdTypeToEdit] = useState<number>();
  const isBeaconChannel = values.channelType === ChannelType.BEACON;
  const outdoorRelatedColumns = useMemo(() => {
    if (isBeaconChannel) {
      return [];
    }
    return compact([
      !isEmpty(values.deliveryUnit)
        ? {
            ...getDefaultColumnDescription(
              ChannelGroupAdTypeListColumns.DELIVERY_UNIT,
              i18n.t('channelGroupAdTypeList.headers.deliveryUnit')
            ),
            formatter: () =>
              i18n.t(`deliveryUnit.${values.deliveryUnit!.toLowerCase()}`)
          }
        : undefined,
      {
        ...getDefaultColumnDescription(
          ChannelGroupAdTypeListColumns.SCREEN_DISPLAY_TYPE,
          i18n.t('channelGroupAdTypeList.headers.screenDisplayType')
        ),
        formatter: (value: ScreenDisplayType) => {
          if (!value || value === ScreenDisplayType.SINGLE) {
            return i18n.t(
              `screenDisplayType.${camelCase(values.channelType)}.single`
            );
          }
          return i18n.t(`screenDisplayType.${camelCase(value.toString())}`);
        }
      },
      getStatusDesColumn(
        getDefaultColumnDescription(
          ChannelGroupAdTypeListColumns.STATE,
          i18n.t('channelGroupAdTypeList.headers.state')
        ),
        (record) => {
          if (record.state === CampaignAdTypeState.ACTIVE) {
            return {
              des: i18n.t('channelGroupAdTypeList.labels.active'),
              color: 'success'
            };
          }
          if (record.state === CampaignAdTypeState.DELETED) {
            return {
              des: i18n.t('channelGroupAdTypeList.labels.deleted'),
              color: 'danger'
            };
          }
          return {
            des: i18n.t('channelGroupAdTypeList.labels.inactive'),
            color: 'black'
          };
        }
      ),
      {
        ...getDefaultColumnDescription(
          ChannelGroupAdTypeListColumns.DURATION,
          i18n.t('channelGroupAdTypeList.headers.duration')
        ),
        formatter: (_1, row: CampaignAdTypeSettingFormData) => {
          const bannerTypes = row.bannerTypes;
          const bannerType = bannerTypes[0];
          if (!bannerType) {
            return 'N/A';
          }
          if (!bannerType.maxDuration) {
            return `${bannerType.minDuration}+ ${i18n.t('common.units.seconds')}`;
          }
          return `${bannerType.minDuration} - ${bannerType.maxDuration} ${i18n.t('common.units.seconds')}`;
        }
      },
      {
        ...getDefaultColumnDescription(
          ChannelGroupAdTypeListColumns.SIZE,
          i18n.t('channelGroupAdTypeList.headers.size')
        ),
        formatter: (_1, row: CampaignAdTypeSettingFormData) => {
          const bannerTypes = row.bannerTypes;
          const bannerType = bannerTypes[0];
          if (!bannerType) {
            return 'N/A';
          }
          return `${bannerType.width} x ${bannerType.height}`;
        }
      },
      {
        ...getDefaultColumnDescription(
          ChannelGroupAdTypeListColumns.OUTDOOR_TYPE,
          i18n.t('channelGroupAdTypeList.headers.supportedOutdoorTypes')
        ),
        formatter: (_1, row: CampaignAdTypeSettingFormData) => {
          const bannerTypes = row.bannerTypes;
          const bannerType = bannerTypes[0];
          if (!bannerType || !bannerType.supportedOutdoorTypes) {
            return 'N/A';
          }
          return bannerType.supportedOutdoorTypes
            .map((outdoorType) =>
              i18n.t(`outdoorForm.labels.outdoorType${outdoorType}`)
            )
            .join(', ');
        }
      }
    ]);
  }, [isBeaconChannel, values.deliveryUnit, values.channelType]);

  const columns = useMemo(
    () => [
      getNameIdColumn(
        getDefaultColumnDescription(
          ChannelGroupAdTypeListColumns.ID,
          i18n.t('channelGroupAdTypeList.headers.id')
        ),
        undefined,
        (record) => setAdTypeToEdit(record.adTypeId),
        'adTypeId'
      ),
      ...outdoorRelatedColumns,
      getStringArrayColumn(
        getDefaultColumnDescription(
          ChannelGroupAdTypeListColumns.PRICING_MODEL,
          i18n.t('channelGroupAdTypeList.headers.pricingModel')
        ),
        (value) => i18n.t(`campaign.labels.${value}`)
      )
    ],
    [outdoorRelatedColumns]
  );

  const adTypeSettings = useMemo(
    () =>
      values.campaignAdTypeSettings.filter(
        (setting) => setting.adTypeId !== undefined
      ),
    [values.campaignAdTypeSettings]
  );

  const tmpAdTypeSettings = useMemo(
    () =>
      values.campaignAdTypeSettings.filter(
        (setting) => setting.tmpId !== undefined
      ),
    [values.campaignAdTypeSettings]
  );

  const adTypeIds = useMemo(() => {
    return adTypeSettings.map((setting) => setting.adTypeId!);
  }, [adTypeSettings]);

  const tmpAdTypeIds = useMemo(() => {
    return tmpAdTypeSettings.map((setting) => setting.tmpId!);
  }, [tmpAdTypeSettings]);

  const adTypeIdsKey = useMemo(() => JSON.stringify(adTypeIds), [adTypeIds]);
  const tmpAdTypeIdsKey = useMemo(
    () => JSON.stringify(tmpAdTypeIds),
    [tmpAdTypeIds]
  );

  useEffect(() => {
    const adTypeIds = JSON.parse(adTypeIdsKey);
    const tmpAdTypeIds = JSON.parse(tmpAdTypeIdsKey);
    if (adTypeIds.length === 0) {
      setAdTypesUsageStatus(
        tmpAdTypeIds.reduce(
          (acc: Record<number, boolean>, tmpAdTypeId: number) => ({
            ...acc,
            [tmpAdTypeId]: false
          }),
          {}
        )
      );
      return;
    }
    callAPIs(
      [() => channelGroupManager.getCampaignAdTypesUsageStatus(adTypeIds)],
      (usedStatus: Record<number, boolean>) => setAdTypesUsageStatus(usedStatus)
    );
  }, [adTypeIdsKey, tmpAdTypeIdsKey, callAPIs]);

  const deleteCampaignAdType = useCallback(
    (adTypeId: number) => {
      setValues((prevValues) => ({
        ...prevValues,
        campaignAdTypeSettings: prevValues.campaignAdTypeSettings.map(
          (setting) => {
            if (
              setting.adTypeId !== undefined &&
              setting.adTypeId === adTypeId
            ) {
              return { ...setting, state: CampaignAdTypeState.DELETED };
            }
            return setting;
          }
        )
      }));
    },
    [setValues]
  );

  return {
    loading,
    adTypeSettings: values.campaignAdTypeSettings,
    columns,
    adTypesUsageStatus,
    adTypeToEdit,
    setAdTypeToEdit,
    deleteCampaignAdType
  };
};
